import type { SwingMetrics, ShotType } from '../types.js';

/**
 * Top-level coaching rule category. Broader than `ShotType` because rules may
 * be movement / mental / return-specific rather than tied to a single stroke.
 */
export type ShotCategory =
  | 'serve'
  | 'forehand'
  | 'backhand'
  | 'volley'
  | 'return'
  | 'net'
  | 'movement'
  | 'mental';

/** Bibliographic citation for a rule. Every rule must cite a real source. */
export interface Citation {
  readonly author: string;
  readonly year: number;
  readonly title: string;
  readonly page?: string;
}

/**
 * Session-level aggregates passed alongside a single swing so rules can
 * compare one stroke against a player's own recent behaviour.
 *
 * Fields beyond the required ones may be added over time; rules that need
 * data we don't yet capture should declare them in {@link Rule.requiredData}
 * and the engine will skip those rules cleanly when the data is missing.
 */
export interface Aggregates {
  readonly sessionSwingCount: number;
  readonly medianPeakWristAngVel: number;
  readonly medianElbowAtContactDeg: number;
  readonly rallyCount?: number;
  /** Free-form extras for rules requiring data outside the current typed core. */
  readonly [k: string]: unknown;
}

/**
 * A single evidence-based coaching rule. Rules are deterministic boolean
 * predicates over (swing, aggregates) plus metadata used for UX, filtering,
 * and auditability.
 */
export interface Rule {
  /** Stable short ID, e.g. `serve.trophy.elbow-collapse`. */
  readonly id: string;
  readonly category: ShotCategory;
  /** If set, rule only applies to swings classified as this shot type. */
  readonly shotType?: ShotType;
  readonly conditionDescription: string;
  readonly condition: (swing: SwingMetrics, agg: Aggregates) => boolean;
  /** Layperson-friendly Brazilian Portuguese feedback shown to the player. */
  readonly feedbackPt: string;
  readonly severity: 'info' | 'tip' | 'warning';
  readonly source: Citation;
  /** 0..1 — confidence the rule's threshold reflects literature consensus. */
  readonly confidence: number;
  /** True for every rule in this first pass — human review gating. */
  readonly needsExpertReview: boolean;
  /** Keys on `Aggregates` (or extensions) that must exist for the rule to run. */
  readonly requiredData?: readonly string[];
  readonly notes?: string;
  /**
   * IDs of `RootCause` entries this symptom tends to indicate. The causal
   * graph engine sums `weight` across fired rules per cause to produce a
   * probability estimate. Optional for backward compatibility.
   */
  readonly causes?: readonly string[];
  /**
   * IDs of other rules that frequently co-occur with this one. Hints for
   * explanation & dedup ("when X fires we often also see Y") — not used
   * to fire rules transitively.
   */
  readonly implies?: readonly string[];
  /**
   * Contribution weight in [0, 1] when this rule fires, argued toward each
   * of its `causes`. Defaults to 0.5 in the engine when omitted.
   */
  readonly weight?: number;
}

/** A rule that matched against a swing + aggregates. */
export interface RuleHit {
  readonly rule: Rule;
  readonly fired: true;
  /**
   * When a root cause consolidates this symptom (probability > 0.6), the
   * UI should show the cause instead of this hit in isolation. Set by the
   * causal graph pass; absent on raw hits.
   */
  readonly consolidatedBy?: string;
}

/** Severity ordering used by the engine when sorting hits. */
export const SEVERITY_RANK: Readonly<Record<Rule['severity'], number>> = Object.freeze({
  warning: 0,
  tip: 1,
  info: 2,
});

/**
 * Return true iff every key listed in `requiredData` exists as an own
 * property on the aggregates object with a non-undefined value. Keeps rules
 * depending on not-yet-captured data (ball speed, toss height, court
 * surface) dormant until the pipeline supplies them.
 */
export function hasRequiredData(
  agg: Aggregates,
  required: readonly string[] | undefined,
): boolean {
  if (!required || required.length === 0) return true;
  for (const key of required) {
    if (!(key in agg)) return false;
    if ((agg as Record<string, unknown>)[key] === undefined) return false;
  }
  return true;
}
